import { useEffect, useState } from 'react';

import { ScreenSizes } from '../@types';
import { useKioskManager } from './useKioskManager';

type OS = 'Windows' | 'Linux' | 'macOS' | 'iOS' | 'Android' | 'Other';
type Browser = 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'IE' | 'Other';
interface OSAndBrowserInfo {
  os: OS | null;
  browser: Browser | null;
  browserVersion: number | null;
}

export const useDeviceInfo = (): OSAndBrowserInfo => {
  const [osAndBrowserInfo, setOsAndBrowserInfo] = useState<OSAndBrowserInfo>({
    os: null,
    browser: null,
    browserVersion: null,
  });

  useEffect(() => {
    const userAgent = window.navigator.userAgent;

    // Detect OS
    if (/Win/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({ ...prevInfo, os: 'Windows' }));
    }
    if (/Linux/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({ ...prevInfo, os: 'Linux' }));
    }
    if (/Mac/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({ ...prevInfo, os: 'macOS' }));
    }
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({ ...prevInfo, os: 'iOS' }));
    }
    if (/Android/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({ ...prevInfo, os: 'Android' }));
    }

    // Browser and BrowserVersion may not be needed or used anywhere. Leaving it here in case we need it
    if (/MSIE|Trident/.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({
        ...prevInfo,
        browser: 'IE',
        browserVersion: parseFloat(userAgent.split('MSIE')[1]) || null,
      }));
    }
    if (/Edge\//.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({
        ...prevInfo,
        browser: 'Edge',
        browserVersion: parseFloat(userAgent.split('Edge/')[1]) || null,
      }));
    }
    if (/Firefox\//.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({
        ...prevInfo,
        browser: 'Firefox',
        browserVersion: parseFloat(userAgent.split('Firefox/')[1]) || null,
      }));
    }
    if (/Safari/.test(userAgent) && /Version\//.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({
        ...prevInfo,
        browser: 'Safari',
        browserVersion: parseFloat(userAgent.split('Version/')[1]) || null,
      }));
    }
    if (/Chrome\//.test(userAgent)) {
      setOsAndBrowserInfo((prevInfo) => ({
        ...prevInfo,
        browser: 'Chrome',
        browserVersion: parseFloat(userAgent.split('Chrome/')[1]) || null,
      }));
    }
  }, []);
  return osAndBrowserInfo;
};

export const useIsSafariUnder16_4 = (): boolean => {
  const { browser, browserVersion } = useDeviceInfo();
  return Boolean(
    browser === 'Safari' && browserVersion && browserVersion < 16.4,
  );
};

export const useIsiOS = (): boolean => {
  const { os } = useDeviceInfo();
  return Boolean(os === 'iOS');
};

export const useIsAndroid = (): boolean => {
  const { os } = useDeviceInfo();
  return Boolean(os === 'Android');
};

export const useIsMobile = (): boolean => {
  const { kioskMode } = useKioskManager();
  const width = window.innerWidth;
  const isAndroid = useIsAndroid();
  const isIos = useIsiOS();
  return Boolean(
    (width <= ScreenSizes.LARGE || isAndroid || isIos) && !kioskMode,
  );
};
